/**
 * FixGuideTabContent
 * Fix Guide tab showing TSG documentation with markdown content
 */

import { makeStyles, tokens, Text, Link } from '@fluentui/react-components'
import { DocumentTextRegular, OpenRegular } from '@fluentui/react-icons'
import type { IssueDetails } from '../../../../domain'
import { MarkdownContentRenderer } from '../panels'
import { FeedbackLink, InlineEmptyState, TabContentLayout } from '../shared'

const useStyles = makeStyles({
  tsgHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  tsgTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  tsgIcon: {
    fontSize: '16px',
    color: tokens.colorBrandForeground1,
  },
  openLink: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  openIcon: {
    fontSize: '14px',
  },
})

export interface FixGuideTabContentProps {
  issue: IssueDetails
}

export function FixGuideTabContent({ issue }: FixGuideTabContentProps) {
  const styles = useStyles()

  // Prefer tsg-research (comprehensive AI-generated guide) over short fix_steps docs
  const tsgResearch = issue.tsgDocuments?.find((doc) => doc.id === 'tsg-research')
  const fixStepsTsg = issue.tsgDocuments?.find((doc) => doc.type === 'fix_steps')
  const generalTsg = issue.tsgDocuments?.find((doc) => doc.type === 'general' && doc.id !== 'tsg-research')
  const activeTsg = tsgResearch || fixStepsTsg || generalTsg

  if (!activeTsg) {
    return (
      <TabContentLayout>
        <InlineEmptyState message="No TSG documentation available for this issue type." withBackground />
        <FeedbackLink context="fix-guide-empty" />
      </TabContentLayout>
    )
  }

  // Use the source TSG doc link when tsg-research is active but has no URL
  const headerUrl = activeTsg.url || fixStepsTsg?.url || generalTsg?.url || ''

  return (
    <TabContentLayout>
      <div className={styles.tsgHeader}>
        <div className={styles.tsgTitle}>
          <DocumentTextRegular className={styles.tsgIcon} />
          <Text weight="semibold">{activeTsg.title}</Text>
        </div>
        {headerUrl && (
          <Link
            href={headerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.openLink}
          >
            Open in new tab
            <OpenRegular className={styles.openIcon} />
          </Link>
        )}
      </div>

      {activeTsg.content && <MarkdownContentRenderer content={activeTsg.content} />}

      <FeedbackLink context="fix-guide" />
    </TabContentLayout>
  )
}
